import { useEffect, useRef, useState } from "react";
import {
  Col,
  Dropdown,
  Menu,
  message,
  Row,
  TableColumnsType,
  Tooltip,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { ReactComponent as MenuIcon } from "@assets/images/vertical-dots.svg";

import Table from "@components/Table";
import Button from "@components/Button";
import { checkPermission } from "@utils";
import { permissions } from "@router";
import {
  ErrorServices,
  jobFunctionsApi,
  useDeleteJobFunctionMutation,
  useFetchJobFunctionsQuery,
  useUploadJobFunctionsMutation,
} from "@services";
import AddJobFunction from "./AddJobFunction";
import EditJobFunction from "./EditJobFunction";
import { useTypedSelector } from "@/hooks";
import customErrorMessage from "@/components/CustomErrorMessage";
import { handleCsvUpload } from "@/utils/uploadCsv";
import { GoPencil } from "react-icons/go";
import { BsTrash3 } from "react-icons/bs";

type JobSubFunctionType = {
  id: number;
  name: string;
};

type JobFunctionType = {
  id: number;
  name: string;
  job_sub_functions: Array<JobSubFunctionType>;
};

const JobFunction = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [page, setPage] = useState(1);
  let jf_id = useRef<any>(null);
  const [pageSize, setPageSize] = useState(10);
  const { user, permissions: per } = useTypedSelector(state => state.auth);
  const [isAddJFVisible, setIsAddJFVisible] = useState(false);
  const [isEditJFVisible, setIsEditJFVisible] = useState(false);
  const {
    data: jobFunctionsData,
    isLoading: isLoadingJobFunctions,
    refetch: refetchJobFunctions,
  } = useFetchJobFunctionsQuery({ page, page_size: pageSize });
  const { data: jobFunctions, pagination } = jobFunctionsData || {};
  const [deleteJobFunction] = useDeleteJobFunctionMutation();
  const [jfItem, setJfItem] = useState({});

  const [uploadJobFunction, { isLoading: isUploading }] =
    useUploadJobFunctionsMutation();

  useEffect(() => {
    refetchJobFunctions();
  }, []);

  // const [downloadJobFunction, { isLoading: isDownloading }] =
  //   useDownloadJobFunctionsMutation();

  const inputRef = useRef<any>(null);

  const handleActionDropdown = ({
    item,
    key,
    domEvent,
  }: {
    key: string;
    domEvent:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>;
    item: JobFunctionType;
  }) => {
    domEvent.stopPropagation();
    if (key === "2") {
      removeJobFunction(item?.id.toString());
    } else {
      onEditJf(item);
    }
  };

  const columns: TableColumnsType<JobFunctionType> = [
    {
      title: "Job Function",
      key: "name",
      dataIndex: "name",
      width: "30%",
    },
    // {
    //   title: "Job Sub-Function",
    //   key: "job_sub_functions",
    //   width: "55%",
    //   render: ({ job_sub_functions }) => {
    //     if (Array.isArray(job_sub_functions) && job_sub_functions.length) {
    //       return job_sub_functions
    //         .map((val: JobSubFunctionType) => {
    //           return val.name;
    //         })
    //         .join(", ");
    //     }
    //     return null;
    //   },
    // },

    {
      title: "Job Sub-Function",
      key: "job_sub_functions",
      width: "70%",
      render: ({ job_sub_functions }) => {
        if (!job_sub_functions?.length) return null;

        const names = job_sub_functions.map((x: JobSubFunctionType) => x.name);

        const visibleCount = 3;
        const remaining = names.length - visibleCount;

        return (
          <Tooltip title={names.join(", ")}>
            <span>
              {names.slice(0, visibleCount).join(", ")}
              {remaining > 0 && (
                <span className="text-blue-500 text-center text-xs font-semibold mx-2">
                  {" ... "}+{remaining}
                </span>
              )}
            </span>
          </Tooltip>
        );
      },
    },

    ...((per?.includes("UPDATE_JOB_FUNCTION") ||
    per?.includes("DELETE_JOB_FUNCTION") ||
    per?.includes("all")
      ? [
          {
            title: "Actions",
            key: "action",
            width: "15%",
            align: "center",
            render: (item: JobFunctionType) => (
              <div className="flex items-center gap-2">
                {(per?.includes("UPDATE_JOB_FUNCTION") ||
                  per?.includes("all")) && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      onEditJf(item);
                    }}
                    className="table__action__btn table__action__btn--client"
                  >
                    <GoPencil className="text-white mr-1 text-sm" /> Edit
                  </div>
                )}

                {(per?.includes("DELETE_JOB_FUNCTION") ||
                  per?.includes("all")) && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      removeJobFunction(item.id.toString());
                    }}
                    className="table__action__btn table__action__btn--delete"
                  >
                    <BsTrash3 className="text-white textx mr-1" /> Delete
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []) as any),
  ];

  const onRowClick = (data: any) => {
    history.push(`/job-functions/${data.id}`, { id: data.id });
  };

  const onEditJf = async (item: JobFunctionType) => {
    try {
      setJfItem(item);
      setIsEditJFVisible(true);
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const removeJobFunction = async (id: string) => {
    try {
      jf_id.current = id;
      await deleteJobFunction(id).unwrap();
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const downloadFile = () => {
    dispatch(
      jobFunctionsApi.endpoints.downloadJobFunctions.initiate(null, {
        subscribe: false,
        forceRefetch: true,
      })
    );
  };

  return (
    <>
      <AddJobFunction
        setIsVisible={setIsAddJFVisible}
        isVisible={isAddJFVisible}
      />
      <EditJobFunction
        setIsVisible={setIsEditJFVisible}
        isVisible={isEditJFVisible}
        jfItem={jfItem}
      />

      <div className="bg-white p-4 sm:p-5">
        {/* <div className="appTitle text-base font-[500] mx-5">Job Functions</div> */}
        <div className="jobCreationBar  sticky top-0 z-10 bg-themeCore px-14 py-6 rounded-lg flex justify-between items-center mb-7">
          <div className="title text-white font-[500] text-3xl uppercase">
            Job Functions & Sub Functions
          </div>
          {checkPermission(permissions.CREATE_JOB_SUB_FUNCTION) && (
            <div className="action-buttons flex items-center gap-2">
              <input
                id="myInput"
                type="file"
                ref={inputRef}
                hidden={true}
                accept=".csv,text/csv"
                onChange={e => handleCsvUpload(e, uploadJobFunction)}
              />
              <Button
                variant="buttonWhite"
                onClick={() => inputRef?.current?.click()}
              >
                {isUploading ? (
                  <LoadingOutlined color="white" className="spinner" />
                ) : (
                  "Upload Job Functions"
                )}
              </Button>
              <Button variant="buttonWhite" onClick={downloadFile}>
                Download Job Functions
              </Button>

              <Button
                variant="buttonWhite"
                onClick={() => setIsAddJFVisible(true)}
              >
                Add New Job
              </Button>
            </div>
          )}
        </div>
        <div className="rounded-lg">
          <Table
            data={jobFunctions}
            columns={columns}
            onRowClick={onRowClick}
            isLoading={isLoadingJobFunctions}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            onPageSizeChange={setPageSize}
            page={page}
          />
        </div>
      </div>
    </>
  );
};

export default JobFunction;
